import React, { useState } from 'react'
import { z } from 'zod'
import useSWRMutation from 'swr/mutation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PlusIcon } from '@radix-ui/react-icons'
import Loader from '@/components/ui/Loader'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { postFetcher, useAuthStore } from 'api/swr'
import { displayNameFromFields } from 'commons/userDisplayName'
import {
  MyFormInput,
  MyFormSelect,
  MyFromTextarea,
} from 'components/ui/MyFormField'

const CreatePage = ({ mutate }) => {
  const [open, setOpen] = useState(false)
  const user = useAuthStore((state) => state.auth.user)
  const [defaultValues] = useState({
    name: '',
    intro: '',
    character: '1',
    fullscreenType: '0',
    skipDefaultLayers: true,
  })
  const handleClose = () => {
    setOpen(false)
  }
  const { trigger, isMutating, error } = useSWRMutation(
    '/templates',
    postFetcher,
  )
  const onSubmit = (data) => {
    if (isMutating) return
    // 原画布尺寸：方图 / 9:16 都是 2048²，3:4 为 1536×2048
    const uploadData = {
      ...data,
      ow: data.fullscreenType === 3 ? 1536 : 2048,
      oh: 2048,
      publisher: displayNameFromFields(user),
      author: user.id,
    }
    trigger(uploadData, {
      onSuccess: () => {
        handleClose()
        mutate()
      },
    })
  }

  const infoFormSchema = z.object({
    name: z
      .string()
      .min(2, {
        message: 'name must be at least 2 characters.',
      })
      .max(30, {
        message: 'name must not be longer than 30 characters.',
      }),
    intro: z.string().max(640).min(4),
    character: z.preprocess((val) => Number(val), z.number().min(1).max(2)),
    fullscreenType: z.preprocess(
      (val) => Number(val),
      z.number().int().refine((v) => [0, 2, 3].includes(v)),
    ),
    skipDefaultLayers: z.boolean().optional(),
  })

  const form = useForm({
    resolver: zodResolver(infoFormSchema),
    defaultValues,
  })
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="mr-2" />
          Create template
        </Button>
      </DialogTrigger>
      <DialogContent className="dialog-content">
        <DialogHeader>
          <DialogTitle>Create template</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <MyFormInput
              name="name"
              form={form}
              label="Name"
              placeholder={'template name'}
            />
            <MyFromTextarea
              name="intro"
              form={form}
              label="Description"
              placeholder={'template description'}
            />
            {/* 画布尺寸固定 2048，不给画师填 */}
            {/* <MyFormInput
              name="ow"
              type="number"
              form={form}
              label="ow"
              placeholder={'template width'}
            />
            <MyFormInput
              name="oh"
              type="number"
              form={form}
              label="oh"
              placeholder={'template height'}
            /> */}
            <MyFormSelect
              name="character"
              form={form}
              label="character"
              items={[
                { code: '1', name: '1' },
                { code: '2', name: '2' },
              ]}
              placeholder={'character number'}
            />
            <MyFormSelect
              name="fullscreenType"
              form={form}
              label="画幅比例"
              items={[
                { code: '0', name: '1:1（普通）' },
                { code: '2', name: '9:16（全屏）' },
                { code: '3', name: '3:4' },
              ]}
              placeholder={'画幅比例'}
            />
            <FormField
              control={form.control}
              name="skipDefaultLayers"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                  </FormControl>
                  <FormLabel className="font-normal leading-snug">
                    不创建默认层（空模板，供 PSD 导入）
                  </FormLabel>
                </FormItem>
              )}
            />
            {isMutating ? (
              <Loader>Updating...</Loader>
            ) : (
              <Button type="submit">Update</Button>
            )}
            {error && <div className="text-red-500">{error.message}</div>}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default CreatePage
